import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUnreadOrdersCount, getUnreadContactCount, getOrders, getContactSubmissions, markAllOrdersAsRead, markAllContactsAsRead, markOrderAsRead, markContactSubmissionAsRead } from '../services/adminService';
import { adminPath } from '../utils/adminPaths';
import { isPushSupported, subscribeToPush, isSubscribed } from '../pwa/PushNotifications';
import { useToast } from './Toast';
import useContactSettings from '../hooks/useContactSettings';
import './AdminLayout.css';

const isStandalone = typeof window !== 'undefined' && (
  window.navigator.standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches
);
const isIOSStandalone = typeof window !== 'undefined' && window.navigator.standalone === true;
const safeAreaStyle = isIOSStandalone ? { paddingTop: '60px' } : undefined;

// Matches the AdminLayout.css breakpoint where the sidebar becomes an overlay drawer.
const DRAWER_BREAKPOINT = 992;
const isDrawerViewport = () => typeof window !== 'undefined' && window.innerWidth < DRAWER_BREAKPOINT;

const ROLE_LABELS = { admin: 'Administrator', editor: 'Editor', viewer: 'Viewer' };
export const getRoleLabel = (role) => {
  const r = String(role || '').toLowerCase();
  if (ROLE_LABELS[r]) return ROLE_LABELS[r];
  return r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Unknown role';
};

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { contactSettings } = useContactSettings();
  const creditName = contactSettings?.credit_name || 'Ektra Tech';
  const creditUrl = contactSettings?.credit_url || 'https://ektra-eg.com';

  // Force admin dashboard to always be in English (LTR)
  // useLayoutEffect prevents any visual flash of RTL styles
  useLayoutEffect(() => {
    // Save the current language state to restore later
    const savedDir = document.documentElement.dir;
    const savedLang = document.documentElement.lang;
    const hadRtlClass = document.body.classList.contains('rtl');

    // Force English/LTR on the document
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'en';
    document.body.classList.remove('rtl');
    document.body.classList.add('ltr');

    // Restore original language settings when leaving admin
    return () => {
      document.documentElement.dir = savedDir;
      document.documentElement.lang = savedLang;
      if (hadRtlClass) {
        document.body.classList.add('rtl');
        document.body.classList.remove('ltr');
      }
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate(adminPath('/login'));
  };

  // Menu organized by sections
  const menuSections = [
    {
      title: 'Main',
      items: [
        { path: adminPath('/'), label: 'Dashboard', icon: 'la la-dashboard', exact: true },
        { path: adminPath('/insights'), label: 'Insights', icon: 'la la-bar-chart' },
        { path: adminPath('/orders'), label: 'Orders', icon: 'la la-shopping-cart' },
      ]
    },
    {
      title: 'Catalog',
      items: [
        { path: adminPath('/products'), label: 'Products', icon: 'la la-cube' },
        { path: adminPath('/categories'), label: 'Categories', icon: 'la la-folder' },
        { path: adminPath('/brands'), label: 'Brands', icon: 'la la-tags' },
      ]
    },
    {
      title: 'Content',
      items: [
        { path: adminPath('/news'), label: 'News', icon: 'la la-newspaper-o' },
        { path: adminPath('/page-images'), label: 'Page Images', icon: 'la la-image' },
        { path: adminPath('/about-page-settings'), label: 'About Page', icon: 'la la-info-circle' },
      ]
    },
    {
      title: 'Settings',
      items: [
        { path: adminPath('/shipping-cities'), label: 'Shipping Cities', icon: 'la la-truck' },
        { path: adminPath('/promo-codes'), label: 'Promo Codes', icon: 'la la-gift' },
        { path: adminPath('/shipping-settings'), label: 'Shipping', icon: 'la la-cog' },
        { path: adminPath('/dashboard-modules'), label: 'Modules', icon: 'la la-th-large' },
        { path: adminPath('/popup-settings'), label: 'Popup Ad', icon: 'la la-bullhorn' },
        { path: adminPath('/announcement-bar'), label: 'Announcement Bar', icon: 'la la-bullhorn' },
        { path: adminPath('/contact-settings'), label: 'Contact Details', icon: 'la la-phone' },
      ]
    },
    {
      title: 'Communication',
      items: [
        { path: adminPath('/contact-submissions'), label: 'Messages', icon: 'la la-envelope' },
        { path: adminPath('/users'), label: 'Users', icon: 'la la-users' },
      ]
    }
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const toast = useToast();
  // Below 992px the sidebar is an overlay drawer (AdminLayout.css), so it must start
  // closed there; on desktop it starts open (or as last chosen by the user).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (!isDrawerViewport()) {
      try {
        return localStorage.getItem('admin_sidebar_open') !== 'false';
      } catch (_) {
        return true;
      }
    }
    return false;
  });

  useEffect(() => {
    if (isDrawerViewport()) return;
    try { localStorage.setItem('admin_sidebar_open', sidebarOpen ? 'true' : 'false'); } catch (_) { /* ignore */ }
  }, [sidebarOpen]);

  // Close the drawer after navigating on phones/tablets so it never covers the content.
  useEffect(() => {
    if (isDrawerViewport()) setSidebarOpen(false);
  }, [location.pathname]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadOrdersCount, setUnreadOrdersCount] = useState(0);
  const [unreadContactsCount, setUnreadContactsCount] = useState(0);
  const [unreadOrders, setUnreadOrders] = useState([]);
  const [unreadContacts, setUnreadContacts] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushAvailable, setPushAvailable] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [bellShake, setBellShake] = useState(false);
  const prevCountsRef = useRef({ orders: 0, contacts: 0 });

  useEffect(() => {
    fetchUnreadCounts(true);
    const interval = setInterval(() => fetchUnreadCounts(false), 10000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadCounts(false);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', () => fetchUnreadCounts(false));

    const handleSWMessage = (event) => {
      if (event.data?.type === 'NEW_NOTIFICATION') fetchUnreadCounts(false);
    };
    navigator.serviceWorker?.addEventListener('message', handleSWMessage);

    const checkPush = async () => {
      if (!isPushSupported()) return;
      setPushAvailable(true);
      const sub = await isSubscribed();
      setPushSubscribed(sub);
    };
    checkPush();

    if (!isStandalone) {
      const installHandler = (e) => { e.preventDefault(); setDeferredInstallPrompt(e); };
      window.addEventListener('beforeinstallprompt', installHandler);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', handleVisibility);
        navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
        window.removeEventListener('beforeinstallprompt', installHandler);
      };
    }
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInstallApp = useCallback(async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') setDeferredInstallPrompt(null);
    }
  }, [deferredInstallPrompt]);

  const fetchUnreadCounts = async (isInitial = false) => {
    try {
      const [ordersResult, contactsResult] = await Promise.all([
        getUnreadOrdersCount(),
        getUnreadContactCount()
      ]);
      const newOrders = ordersResult.count || 0;
      const newContacts = contactsResult.count || 0;
      const prev = prevCountsRef.current;

      if (!isInitial) {
        const addedOrders = newOrders - prev.orders;
        const addedContacts = newContacts - prev.contacts;
        if (addedOrders > 0) {
          toast.info(`${addedOrders} new order${addedOrders > 1 ? 's' : ''} received`);
          setBellShake(true);
          setTimeout(() => setBellShake(false), 1000);
        }
        if (addedContacts > 0) {
          toast.info(`${addedContacts} new message${addedContacts > 1 ? 's' : ''} received`);
          setBellShake(true);
          setTimeout(() => setBellShake(false), 1000);
        }
      }

      prevCountsRef.current = { orders: newOrders, contacts: newContacts };
      setUnreadOrdersCount(newOrders);
      setUnreadContactsCount(newContacts);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  const handleNotificationClick = async () => {
    if (showNotifications) {
      setShowNotifications(false);
      fetchUnreadCounts();
      return;
    }

    setShowNotifications(true);
    setLoadingNotifications(true);

    try {
      const [allOrders, allContacts] = await Promise.all([
        getOrders(),
        getContactSubmissions()
      ]);

      const unreadOrdersList = allOrders.filter(order => order.is_read === false).slice(0, 10);
      const unreadContactsList = allContacts.filter(contact => !contact.is_read).slice(0, 10);

      setUnreadOrders(unreadOrdersList);
      setUnreadContacts(unreadContactsList);
      setUnreadOrdersCount(allOrders.filter(o => o.is_read === false).length);
      setUnreadContactsCount(allContacts.filter(c => !c.is_read).length);
    } catch (error) {
      console.error('Error fetching unread items:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleOrderNotificationClick = async (orderId) => {
    setShowNotifications(false);
    try {
      await markOrderAsRead(orderId);
      setUnreadOrders(prev => prev.filter(o => o.id !== orderId));
      setUnreadOrdersCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const handleContactNotificationClick = async (contactId) => {
    setShowNotifications(false);
    try {
      await markContactSubmissionAsRead(contactId);
      setUnreadContacts(prev => prev.filter(c => c.id !== contactId));
      setUnreadContactsCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await Promise.all([
        markAllOrdersAsRead(),
        markAllContactsAsRead()
      ]);
      setUnreadOrders([]);
      setUnreadContacts([]);
      setUnreadOrdersCount(0);
      setUnreadContactsCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const totalUnread = unreadOrdersCount + unreadContactsCount;

  return (
    <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`} dir="ltr" lang="en">
      <div className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header" style={safeAreaStyle}>
          {sidebarOpen ? (
            <Link to={adminPath('/')} className="sidebar-logo">
              <img src="/assets/images/logo/logo-white.png" alt="Logo" />
            </Link>
          ) : (
            <div className="sidebar-logo-placeholder"></div>
          )}
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <i className={sidebarOpen ? 'la la-angle-left' : 'la la-angle-right'}></i>
          </button>
        </div>
        <nav className="sidebar-nav">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              {sidebarOpen && <div className="nav-section-title">{section.title}</div>}
              {section.items.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isActive(item) ? 'active' : ''}`}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <i className={item.icon}></i>
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="la la-user"></i>
            </div>
            {sidebarOpen && (
              <div className="user-details">
                <span className="user-name">{user?.username}</span>
                <span className="user-role">{getRoleLabel(user?.role)}</span>
              </div>
            )}
          </div>
          {!isStandalone && deferredInstallPrompt && (
            <button className="sidebar-install-btn" onClick={handleInstallApp} title="Install App">
              <i className="la la-download"></i>
              {sidebarOpen && <span>Install App</span>}
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <i className="la la-sign-out"></i>
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay" 
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <div className="admin-main">
        <div className="admin-header" style={safeAreaStyle}>
          <div className="header-left">
            <button 
              className="mobile-menu-toggle" 
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <i className="la la-bars"></i>
            </button>
            <h1>Admin Dashboard</h1>
          </div>
          <div className="header-right">
            {!isStandalone && deferredInstallPrompt && (
              <button
                className="admin-install-btn"
                onClick={handleInstallApp}
                title="Install App"
              >
                <i className="la la-download"></i>
              </button>
            )}
            {isStandalone && !pushSubscribed && pushAvailable && (
              <button
                className="push-subscribe-btn"
                onClick={async () => {
                  const sub = await subscribeToPush('admin');
                  if (sub) setPushSubscribed(true);
                }}
                title="Enable push notifications"
              >
                <i className="la la-bell"></i>
                <span className="push-subscribe-label">Enable</span>
              </button>
            )}
            <button 
              className={`notification-bell${bellShake ? ' bell-shake' : ''}`}
              onClick={handleNotificationClick}
              title="Notifications"
            >
              <i className="la la-bell"></i>
              {totalUnread > 0 && (
                <span className="notification-badge">{totalUnread}</span>
              )}
            </button>
            <span className="user-info">
              <i className="la la-user"></i>
              {user?.username}
            </span>
          </div>
        </div>

        {/* Notifications Modal */}
        {showNotifications && (
          <>
            <div className="notification-overlay" onClick={() => { setShowNotifications(false); fetchUnreadCounts(); }}></div>
            <div className="notification-modal">
              <div className="notification-modal-header">
                <h3>Notifications</h3>
                <div className="notification-header-actions">
                  {totalUnread > 0 && (
                    <button className="mark-all-read-btn" onClick={handleMarkAllRead}>
                      <i className="la la-check-double"></i>
                      Mark All Read
                    </button>
                  )}
                  <button 
                    className="notification-close" 
                    onClick={() => { setShowNotifications(false); fetchUnreadCounts(); }}
                  >
                    <i className="la la-times"></i>
                  </button>
                </div>
              </div>
              <div className="notification-modal-content">
                {loadingNotifications ? (
                  <div className="notification-loading">
                    <i className="la la-spinner la-spin"></i> Loading...
                  </div>
                ) : (
                  <>
                    {/* Unread Orders Section */}
                    <div className="notification-section">
                      <div className="notification-section-header">
                        <i className="la la-shopping-cart"></i>
                        <h4>Unread Orders ({unreadOrdersCount})</h4>
                        {unreadOrdersCount > 0 && (
                          <Link 
                            to={`${adminPath('/orders')}?filter=unread`}
                            className="notification-view-all"
                            onClick={() => { setShowNotifications(false); fetchUnreadCounts(); }}
                          >
                            View All
                          </Link>
                        )}
                      </div>
                      {unreadOrders.length === 0 ? (
                        <div className="notification-empty">
                          <i className="la la-check-circle"></i>
                          <p>No unread orders</p>
                        </div>
                      ) : (
                        <div className="notification-list">
                          {unreadOrders.map(order => (
                            <Link
                              key={order.id}
                              to={`${adminPath('/orders')}?orderId=${order.id}`}
                              className="notification-item"
                              onClick={() => handleOrderNotificationClick(order.id)}
                            >
                              <div className="notification-item-content">
                                <div className="notification-item-title">
                                  Order #{order.order_number}
                                </div>
                                <div className="notification-item-meta">
                                  {order.customer_name} • {new Date(order.created_at).toLocaleDateString()}
                                </div>
                              </div>
                              <i className="la la-chevron-right"></i>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Unread Contact Messages Section */}
                    <div className="notification-section">
                      <div className="notification-section-header">
                        <i className="la la-envelope"></i>
                        <h4>Unread Messages ({unreadContactsCount})</h4>
                        {unreadContactsCount > 0 && (
                          <Link 
                            to={`${adminPath('/contact-submissions')}?filter=unread`}
                            className="notification-view-all"
                            onClick={() => { setShowNotifications(false); fetchUnreadCounts(); }}
                          >
                            View All
                          </Link>
                        )}
                      </div>
                      {unreadContacts.length === 0 ? (
                        <div className="notification-empty">
                          <i className="la la-check-circle"></i>
                          <p>No unread messages</p>
                        </div>
                      ) : (
                        <div className="notification-list">
                          {unreadContacts.map(contact => (
                            <Link
                              key={contact.id}
                              to={adminPath('/contact-submissions')}
                              className="notification-item"
                              onClick={() => handleContactNotificationClick(contact.id)}
                            >
                              <div className="notification-item-content">
                                <div className="notification-item-title">
                                  {contact.subject || 'No Subject'}
                                </div>
                                <div className="notification-item-meta">
                                  {contact.name} • {new Date(contact.created_at).toLocaleDateString()}
                                </div>
                              </div>
                              <i className="la la-chevron-right"></i>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <div className="admin-content">
          {children}
        </div>

        <div className="admin-footer">
          Website Is Designed &amp; Created By |{' '}
          <a
            href={creditUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-footer-link"
          >
            {creditName}
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
